import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";

import { CompletionStatus, MedicalStatus } from "../types/processTypes";

export interface MedicalValue {
  medicalStatus?: MedicalStatus;
  medicalBookedDate?: Date | string;
  medicalExpiryDate?: Date | string;
  cocCompleted?: boolean;
}

interface Props {
  value: MedicalValue;
  onChange: (patch: Partial<MedicalValue>) => void;
  disabled?: boolean;
  locale?: string;
  compact?: boolean;
  showCoc?: boolean;
}

export const MedicalFields = ({
  value,
  onChange,
  disabled,
  locale = "en",
  compact = false,
  showCoc = false,
}: Props) => {
  const { t: te } = useTranslation("enums");
  const status = value.medicalStatus ?? MedicalStatus.not_done;
  const showBooked = status === MedicalStatus.booked;
  const showExpiry =
    status === MedicalStatus.fit || status === MedicalStatus.expired;

  const dateSlotSx = (visible: boolean) => ({
    minHeight: 56,
    visibility: visible ? "visible" : "hidden",
    pointerEvents: visible ? "auto" : "none",
  });

  return (
    <Box sx={{ width: "100%", minHeight: compact ? 0 : 200 }}>
      <Grid container spacing={compact ? 1.5 : 2}>
        {showCoc && (
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label={te("coc")}
              value={
                value.cocCompleted
                  ? CompletionStatus.completed
                  : CompletionStatus.not_completed
              }
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  cocCompleted: e.target.value === CompletionStatus.completed,
                })
              }
            >
              {[CompletionStatus.completed, CompletionStatus.not_completed].map(
                (option) => (
                  <MenuItem key={option} value={option}>
                    {te(option)}
                  </MenuItem>
                ),
              )}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size="small"
            label={te("medical")}
            value={status}
            disabled={disabled}
            onChange={(e) =>
              onChange({ medicalStatus: e.target.value as MedicalStatus })
            }
          >
            {Object.keys(MedicalStatus).map((option) => (
              <MenuItem key={option} value={option}>
                {te(option)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sx={dateSlotSx(showBooked)}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
            <DatePicker
              label={te("booked")}
              views={["year", "month", "day"]}
              disableMaskedInput
              inputFormat="DD-MMM-YYYY"
              value={
                value.medicalBookedDate
                  ? new Date(value.medicalBookedDate as Date | string)
                  : null
              }
              onChange={(date) => onChange({ medicalBookedDate: date as Date })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  disabled={disabled || !showBooked}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sx={dateSlotSx(showExpiry)}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
            <DatePicker
              label={te("expired")}
              views={["year", "month", "day"]}
              disableMaskedInput
              inputFormat="DD-MMM-YYYY"
              value={
                value.medicalExpiryDate
                  ? new Date(value.medicalExpiryDate as Date | string)
                  : null
              }
              onChange={(date) => onChange({ medicalExpiryDate: date as Date })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  disabled={disabled || !showExpiry}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    </Box>
  );
};
